/**
 * Date utility functions for ISO 8601 UTC date formatting.
 * All date values sent to the backend must be in ISO 8601 UTC format
 * (e.g. "2026-04-02T10:12:42.000Z") for PostgreSQL TIMESTAMPTZ compatibility.
 *
 * Convention:
 * - API / DB naive ISO (YYYY-MM-DDTHH:mm:ss without Z) = UTC
 * - Date-only YYYY-MM-DD = UTC midnight
 * - DD-MM-YYYY / DD/MM/YYYY (user/legacy) = local calendar wall clock
 */

const pad2 = (n: number) => n.toString().padStart(2, "0");

const hasExplicitTimezone = (value: string): boolean =>
  value.includes("Z") || /[+-]\d{2}:?\d{2}$/.test(value);

/**
 * Parse any date-like value into a Date, treating naive API ISO as UTC.
 */
export const parseApiDate = (value: any): Date | null => {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  if (typeof value !== "string") {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  const trimmed = value.trim();
  if (!trimmed) return null;

  // DD-MM-YYYY [HH:mm:ss] — local calendar (user / legacy UI)
  const ddmmyyyy = trimmed.match(
    /^(\d{1,2})-(\d{1,2})-(\d{4})(?:\s+(\d{1,2}):(\d{1,2}):(\d{1,2}))?$/
  );
  if (ddmmyyyy) {
    const [, day, month, year, h, m, s] = ddmmyyyy;
    const date = new Date(
      +year,
      +month - 1,
      +day,
      +(h || 0),
      +(m || 0),
      +(s || 0)
    );
    return isNaN(date.getTime()) ? null : date;
  }

  // DD/MM/YYYY [HH:mm:ss] — local calendar
  const ddmmyyyySlash = trimmed.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{1,2}):(\d{1,2}))?$/
  );
  if (ddmmyyyySlash) {
    const [, day, month, year, h, m, s] = ddmmyyyySlash;
    const date = new Date(
      +year,
      +month - 1,
      +day,
      +(h || 0),
      +(m || 0),
      +(s || 0)
    );
    return isNaN(date.getTime()) ? null : date;
  }

  // YYYY-MM-DD date-only → UTC midnight
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const date = new Date(`${trimmed}T00:00:00Z`);
    return isNaN(date.getTime()) ? null : date;
  }

  // ISO / datetime-local: YYYY-MM-DDTHH:mm[:ss][.sss][Z|offset]
  if (trimmed.includes("T")) {
    // Form state from toDateTimeInputValue: YYYY-MM-DDTHH:mm (no seconds, no Z) = local
    const isFormLocalMinute = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed);
    if (!hasExplicitTimezone(trimmed) && isFormLocalMinute) {
      const [datePart, timePart] = trimmed.split("T");
      const [year, month, day] = datePart.split("-").map(Number);
      const [hours, minutes] = timePart.split(":").map(Number);
      const date = new Date(year, month - 1, day, hours, minutes, 0);
      return isNaN(date.getTime()) ? null : date;
    }

    // Naive API ISO (with seconds/ms, no Z) and explicit Z/offset → UTC-aware
    let parseValue = trimmed;
    if (!hasExplicitTimezone(trimmed)) {
      parseValue = `${trimmed}Z`;
    }
    const date = new Date(parseValue);
    return isNaN(date.getTime()) ? null : date;
  }

  // Space-separated YYYY-MM-DD HH:mm:ss (naive API) → UTC
  const ymdSpace = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?/
  );
  if (ymdSpace) {
    const [, year, month, day, h, m, s] = ymdSpace;
    const date = new Date(
      Date.UTC(+year, +month - 1, +day, +h, +m, +(s || 0))
    );
    return isNaN(date.getTime()) ? null : date;
  }

  const fallback = new Date(trimmed);
  return isNaN(fallback.getTime()) ? null : fallback;
};

/**
 * Convert any date value to ISO 8601 UTC string.
 */
export const toUTC = (value: any): string | null => {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // Date-only stays UTC midnight (explicit, before parseApiDate)
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const date = new Date(`${trimmed}T00:00:00Z`);
      return isNaN(date.getTime()) ? null : date.toISOString();
    }
  }

  const date = parseApiDate(value);
  return date ? date.toISOString() : null;
};

/**
 * Local YYYY-MM-DD for HTML date inputs, after UTC-aware parse.
 */
export const toDateInputValue = (value: any): string => {
  const date = parseApiDate(value);
  if (!date) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

/**
 * Local YYYY-MM-DDTHH:mm for datetime-local / form state, after UTC-aware parse.
 */
export const toDateTimeInputValue = (value: any): string => {
  const date = parseApiDate(value);
  if (!date) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

/**
 * Today's local calendar date as UTC midnight ISO (for dateField DefaultToday).
 */
export const todayDateOnlyUtcIso = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = pad2(now.getMonth() + 1);
  const d = pad2(now.getDate());
  return `${y}-${m}-${d}T00:00:00.000Z`;
};

/**
 * Current instant as ISO UTC (for datetimeField DefaultToday).
 */
export const nowUtcIso = (): string => new Date().toISOString();

/**
 * Convert any date value to a local display string (DD-MM-YYYY HH:mm:ss or DD-MM-YYYY).
 */
export const toLocalDisplay = (value: any, includeTime: boolean = false): string => {
  if (value === null || value === undefined || value === "") return "";

  const date = parseApiDate(value);
  if (!date) return String(value);

  const datePart = `${pad2(date.getDate())}-${pad2(date.getMonth() + 1)}-${date.getFullYear()}`;

  if (includeTime) {
    const timePart = `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
    return `${datePart} ${timePart}`;
  }

  return datePart;
};
